import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  REACT_APP_POOL_TIME_2,
  REACT_APP_ENABLE_SERVERSENTEVENT,
} from '../config';
import { useSignalRContext } from '../contexts/SignalRContext';
import { convertToMicrosoftDateFormat } from '../services/SignalRService';
import {
  GetConversations,
  GetMessagesForConversations,
  GetConversationsArchive,
} from '../services/conversations';
import { MarkMessageReceive, MarkAllMessageReceived } from '../services/chat';
import { getUserGuid } from '../storage';
import { useAuth } from './auth';
import { useGroups } from './groups';
import { convertBackendDateLastMessage } from '../utils';
import { useChatMessages } from './chat';
// import { getCallEvent } from '../services/conversations';
import { Alert } from '@material-ui/lab';
import { useIntl } from 'react-intl';
import { SetRSAKey, EncryptMessage } from '../services/e2ee';
import AlertDialog from '../components/alertdialog';
import { getE2EEncryptionEnabledFlag } from '../storage/index';
import loadingIcon from '../assets/images/loading.gif';
import CircularProgress from '@material-ui/core/CircularProgress';
import colors from '../styles/colors';
import { getDecryptedConversationKeyFromCache } from '../services/conversationKeyCache';

const ConversationContext = createContext();
const ConversationProvider = ({ children }) => {
  const { user, portal, logout } = useAuth();
  const [conversations, setConversations] = useState([]);
  const {
    isConnected,
    markAllMessageReceived_SignalR,
    subscribeReceiveMessage,
    subscribeConversationSummary,
    signalRService,
  } = useSignalRContext();
  // const [getCallEvents, setCallEvents] = useState([]);
  const [archiveds, setArchiveds] = useState([]);
  const [conversation, setConversation] = useState();
  const [filterName, setFilterName] = useState('');
  const { staffGroups, reloadGroups } = useGroups();
  const [openInfo, setOpenInfo] = useState(false);
  const userGuid = getUserGuid();
  const [openChat, setOpenChat] = useState(true);
  const [coversationError, setConversationError] = useState(false);
  const [hasCall, setHasCall] = React.useState(0);
  const [isSSEConnectionActive, setSSEConnectionActive] = React.useState(false);
  const intl = useIntl();
  const [conversationDecryptedKey, setConversationDecryptedKey] = useState([]);
  const [conversationDecryptedIV, setConversationDecryptedIV] = useState([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [isDecrypting, setIsDecrypting] = useState(false);
  const hasLoadedOnceRef = React.useRef(false);
  const processedLastMessageGuids = React.useRef({});
  const isCallingRef = React.useRef(false);

  // Real-time SignalR listeners (safely wrapped in useEffect to run ONCE)
  useEffect(() => {
    if (!user) return;

    // 1. When a message is received, update the left-side conversation list in-memory
    const unsubMsg = subscribeReceiveMessage((res) => {
      if (!res?.ConversationId) return;

      const incomingMsg = Array.isArray(res.StaffMessage)
        ? res.StaffMessage[0]
        : res.StaffMessage;

      if (!incomingMsg) return;

      // If user is not currently inside this conversation and message is from someone else, mark as received
      if (
        signalRService.joinedConversationId !== res.ConversationId &&
        `${incomingMsg?.Sender?.StaffGuid}` !== `${userGuid}`
      ) {
        if (signalRService.isConnected || signalRService.isStarting) {
          markAllMessageReceived_SignalR({
            conversationGuid: res.ConversationId,
          });
        } else {
          MarkAllMessageReceived({
            conversationGuid: res.ConversationId,
          });
        }
      }

      setConversations((prevConversations) => {
        const index = prevConversations.findIndex(
          (c) => c.Guid === res.ConversationId
        );

        // If it is a new chat not yet in the list, fetch conversations once from server
        if (index === -1) {
          GetUserConversations();
          return prevConversations;
        }

        const targetConv = { ...prevConversations[index] };
        targetConv.LastMessage = incomingMsg;
        targetConv.time = convertBackendDateLastMessage(incomingMsg);

        // If user is not currently inside this conversation, increment unread badge
        if (signalRService.joinedConversationId !== res.ConversationId) {
          targetConv.NumberOfUnreadMessages =
            (targetConv.NumberOfUnreadMessages || 0) + 1;
        }

        // Move the updated conversation to the very top (index 0)
        return [
          targetConv,
          ...prevConversations.filter((c) => c.Guid !== res.ConversationId),
        ];
      });
    });

    // 2. When conversation summary updates (unread counts, favorites)
    const unsubSummary = subscribeConversationSummary((summaries) => {
      if (!summaries || summaries.length === 0) return;

      setConversations((prevConversations) => {
        let updatedList = [...prevConversations];

        summaries.forEach((item) => {
          const index = updatedList.findIndex(
            (c) => c.Guid === item.ConversationId
          );

          if (index !== -1) {
            const conv = { ...updatedList[index] };
            conv.NumberOfUnreadMessages = item.UnreadMessageCount;
            if (item.IsNotificationMute !== undefined)
              conv.IsNotificationMute = item.IsNotificationMute;
            if (item.IsFavourite !== undefined)
              conv.IsFavourite = item.IsFavourite;

            if (item.StaffMessage) {
              conv.LastMessage = {
                ...item.StaffMessage,
                MessageDateTime: convertToMicrosoftDateFormat(
                  item.StaffMessage.MessageDateTime
                ),
              };
              conv.time = convertBackendDateLastMessage(conv.LastMessage);

              // If user is not currently inside this conversation, mark as received/delivered
              if (
                signalRService.joinedConversationId !== item.ConversationId &&
                conv.LastMessage?.Guid &&
                !conv.LastMessage.IsDelivered &&
                `${conv.LastMessage?.Sender?.StaffGuid}` !== `${userGuid}`
              ) {
                if (signalRService.isConnected || signalRService.isStarting) {
                  markAllMessageReceived_SignalR({
                    conversationGuid: item.ConversationId,
                  });
                } else {
                  MarkAllMessageReceived({
                    conversationGuid: item.ConversationId,
                  });
                }
              }
            }

            updatedList = [
              conv,
              ...updatedList.filter((c) => c.Guid !== item.ConversationId),
            ];
          } else {
            // New conversation / group created elsewhere (e.g., from Desktop)
            GetUserConversations();
          }
        });

        return updatedList;
      });
    });

    return () => {
      unsubMsg();
      unsubSummary();
    };
  }, [
    user,
    subscribeReceiveMessage,
    subscribeConversationSummary,
    signalRService,
  ]);

  // Reset hasLoadedOnceRef to false whenever the user logs out
  useEffect(() => {
    if (!portal) {
      hasLoadedOnceRef.current = false;
    }
  }, [portal]);

  const [
    conversationArchieveDecryptedKey,
    setConversationArchieveDecryptedKey,
  ] = useState([]);
  const [conversationArchieveDecryptedIV, setConversationArchieveDecryptedIV] =
    useState([]);

  const isE2EEnabled = getE2EEncryptionEnabledFlag();

  const reloadConversations = useCallback(async () => {
    if (user) {
      const data = await GetConversations();
      setConversations(data.conversations);
      return data.conversations;
    }
  }, [user]);

  const reloadArchivedConversations = useCallback(async () => {
    if (user) {
      const data = await GetConversationsArchive();
      setArchiveds(data.conversations);
      return data.conversations;
    }
  }, [user]);

  const showAlert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  // const GetUserConversations = async () => {
  //   try {
  //     const data = await GetConversations();
  //     if (data.conversations && data.conversations.length > 0) {
  //       let list = [];

  //       if (isE2EEnabled) {
  //         setConversationDecryptedKey(data.decryptedKeys);
  //         setConversationDecryptedIV(data.decryptedIVs);
  //       }

  //       for (let i = 0; i < data.conversations.length; i++) {
  //         let Conversation = data.conversations[i];

  //         Conversation.isGroup = !Conversation.Owner.FirstName;

  //         list.push({
  //           ...Conversation,
  //           time: convertBackendDateLastMessage(Conversation.LastMessage),
  //         });
  //       }

  //       list.sort((a, b) => {
  //         if (a.time > b.time) {
  //           return -1;
  //         }
  //         if (a.time < b.time) {
  //           return 1;
  //         }
  //         return 0;
  //       });
  //       if (list.length > 0) {
  //         setConversations(
  //           list.filter((c) =>
  //             c.Owner.Name.toUpperCase().includes(filterName.toUpperCase())
  //           )
  //         );
  //       }

  //       for (let i = 0; i < list.length; i++) {
  //         const Conversation = list[i];
  //         if (Conversation.NumberOfUnreadMessages !== 0) {
  //           await GetUserMessagesForConversations({
  //             Guid: Conversation.Guid,
  //             maxNumberMessages: 40,
  //           });
  //         }
  //       }
  //     }

  //     if (data) {
  //       // sequential process
  //       await GetUserConversationsArchive();
  //     }
  //     //});
  //   } catch (error) {
  //     console.log(error);
  //     showAlert(
  //       intl.formatMessage({ id: 'Error' }),
  //       intl.formatMessage({ id: 'RSAKeysNotMatched' })
  //     );
  //   }
  // };

  const GetUserConversations = async () => {
    if (isCallingRef.current) return;
    isCallingRef.current = true;
    try {
      // Check the ref value (always up-to-date, never stale)
      if (isE2EEnabled && !hasLoadedOnceRef.current) {
        setIsDecrypting(true);
      }
      const data = await GetConversations();
      if (data.conversations && data.conversations.length > 0) {
        let list = [];

        if (isE2EEnabled) {
          setConversationDecryptedKey(data.decryptedKeys);
          setConversationDecryptedIV(data.decryptedIVs);
        }

        for (let i = 0; i < data.conversations.length; i++) {
          let Conversation = data.conversations[i];

          Conversation.isGroup = !Conversation.Owner.FirstName;

          list.push({
            ...Conversation,
            time: convertBackendDateLastMessage(Conversation.LastMessage),
          });
        }

        list.sort((a, b) => {
          if (a.time > b.time) {
            return -1;
          }
          if (a.time < b.time) {
            return 1;
          }
          return 0;
        });

        setConversations(list);

        // Mark all unread messages as received in a single batch call per conversation
        for (let i = 0; i < list.length; i++) {
          const conversationItem = list[i];
          const lastMsgGuid = conversationItem.LastMessage?.Guid;

          if (conversationItem.NumberOfUnreadMessages !== 0 && lastMsgGuid) {
            if (
              processedLastMessageGuids.current[conversationItem.Guid] ===
              lastMsgGuid
            ) {
              continue;
            }
            processedLastMessageGuids.current[conversationItem.Guid] =
              lastMsgGuid;

            if (signalRService.isConnected || signalRService.isStarting) {
              markAllMessageReceived_SignalR({
                conversationGuid: conversationItem.Guid,
              });
            } else {
              MarkAllMessageReceived({
                conversationGuid: conversationItem.Guid,
              });
            }
          }
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsDecrypting(false);
      hasLoadedOnceRef.current = true; // Mark as loaded instantly
      isCallingRef.current = false;
    }
  };

  const GetUserConversationsArchive = async () => {
    try {
      const data = await GetConversationsArchive();
      if (data.conversations && data.conversations.length > 0) {
        let list = [];

        if (isE2EEnabled) {
          setConversationArchieveDecryptedKey(data.decryptedKeys);
          setConversationArchieveDecryptedIV(data.decryptedIVs);
        }

        for (let i = 0; i < data.conversations.length; i++) {
          let Conversation = data.conversations[i];

          Conversation.isGroup = !Conversation.Owner.FirstName;
          list.push({
            ...Conversation,
            time: convertBackendDateLastMessage(Conversation.LastMessage),
          });
        }

        list.sort((a, b) => {
          if (a.time > b.time) {
            return -1;
          }
          if (a.time < b.time) {
            return 1;
          }
          return 0;
        });
        if (list.length > 0) {
          setArchiveds(
            list.filter((c) =>
              c.Owner.Name.toUpperCase().includes(filterName.toUpperCase())
            )
          );
        }
        for (let i = 0; i < list.length; i++) {
          const Conversation = list[i];
          if (Conversation.NumberOfUnreadMessages !== 0) {
            await GetUserMessagesForConversations({
              Guid: Conversation.Guid,
              maxNumberMessages: 40,
            });
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const GetUserMessagesForConversations = async ({
    Guid,
    indexMessageGuid = '',
    maxNumberMessages = -1,
    keys = {},
    Iv = {},
  }) => {
    try {
      await GetMessagesForConversations({
        Guid: Guid,
        maxNumberMessages: maxNumberMessages,
        ...(isE2EEnabled && {
          keys,
          Iv: [],
        }),
      }).then((data) => {
        if (data.GetMessagesForConversationResult) {
          data.orderedMessages.forEach(async (message) => {
            if (
              !message.IsDelivered &&
              `${message.Sender.StaffGuid}` !== `${userGuid}`
            ) {
              await MarkMessageReceive({
                messageGuid: message.Guid,
              });
            }
          });
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const EncryptMessageForConversation = async (message, keyObj, ivObj) => {
    try {
      if (!keyObj || !ivObj) return message;
      const data = await EncryptMessage(message, keyObj.Key, ivObj.IV);
      return data;
    } catch (error) {
      console.log(error);
    }
  };

  const SetUserRSAKey = async () => {
    try {
      const data = await SetRSAKey();
    } catch (error) {
      console.log(error);
    }
  };

  const getDecryptionParams = (conversationGuid) => {
    let keyObj = conversationDecryptedKey.find(
      (item) => item.Guid === conversationGuid
    );

    let ivObj = conversationDecryptedIV.find(
      (item) => item.Guid === conversationGuid
    );

    // Fallback to cache for newly created groups
    if (!keyObj || !ivObj) {
      const cached = getDecryptedConversationKeyFromCache(conversationGuid);
      if (cached?.key && cached?.iv) {
        keyObj = { Guid: conversationGuid, Key: cached.key };
        ivObj = { Guid: conversationGuid, IV: cached.iv };
      }
    }

    if (keyObj && ivObj) {
      return { keyObj, ivObj };
    }
    return null;
  };

  useEffect(() => {
    // TODO: no need to call
    // GetUserConversations(); // Initial call for get user conversation

    if (REACT_APP_ENABLE_SERVERSENTEVENT === 'false') {
      const interval = setInterval(async () => {
        if (user && portal) {
          if (document.visibilityState === 'visible') {
            try {
              await GetUserConversations();
            } catch (error) {
              console.log(error);
            }
          }
        }
      }, REACT_APP_POOL_TIME_2);
      return () => clearInterval(interval);
    } else {
      const fetchConversations = async () => {
        if (user && portal) {
          if (document.visibilityState === 'visible') {
            try {
              await GetUserConversations();
            } catch (error) {
              console.log(error);
            }
          }
        }
      };
      fetchConversations();
    }
  }, [user, portal]);

  // Reset all conversation states when user logs out
  useEffect(() => {
    if (!user) {
      setConversations([]);
      setConversation(undefined);
      setConversationDecryptedKey([]);
      setConversationDecryptedIV([]);
      setAlertOpen(false);
    }
  }, [user]);

  // Listen for the force-logout event from conversations.js decryption failures
  useEffect(() => {
    const handleForceLogout = () => {
      if (!alertOpen) {
        showAlert(
          'Login Required',
          'For security reasons, reauthentication is required. Please log in again to continue.'
        );
      }
    };

    window.addEventListener('force-logout', handleForceLogout);
    return () => window.removeEventListener('force-logout', handleForceLogout);
  }, [intl, alertOpen]);

  // const reloadGetCallEvent = useCallback(async () => {
  //   if (user) {
  //     const data = await getCallEvent();

  //     Promise.resolve(data).then((data) => {
  //       if (data?.GetCallEventResult === true) {
  //         setCallEvents(data?.callEvent);
  //       } else {
  //         setCallEvents(null);
  //       }
  //     });

  //     return data;
  //   }
  // }, [user]);

  // useEffect(() => {
  //   const interval = setInterval(async () => {
  //     if (user && portal) {
  //       if (document.visibilityState === 'visible') {
  //         try {
  //           GetConversations().then(async (data) => {
  //             if (data.conversations && data.conversations.length > 0) {
  //               let list = [];
  //               for (let i = 0; i < data.conversations.length; i++) {
  //                 let Conversation = data.conversations[i];
  //                 Conversation.isGroup = !Conversation.Owner.FirstName;
  //                 list.push({
  //                   ...Conversation,
  //                   time: convertBackendDateLastMessage(
  //                     Conversation.LastMessage
  //                   ),
  //                 });
  //               }
  //               list.sort((a, b) => {
  //                 if (a.time > b.time) {
  //                   return -1;
  //                 }
  //                 if (a.time < b.time) {
  //                   return 1;
  //                 }
  //                 return 0;
  //               });
  //               if (list.length > 0)
  //                 setConversations(
  //                   list.filter((c) =>
  //                     c.Owner.Name.toUpperCase().includes(
  //                       filterName.toUpperCase()
  //                     )
  //                   )
  //                 );
  //               for (let i = 0; i < list.length; i++) {
  //                 const Conversation = list[i];
  //                 if (Conversation.NumberOfUnreadMessages !== 0) {
  //                   GetMessagesForConversations({
  //                     Guid: Conversation.Guid,
  //                     maxNumberMessages: 40,
  //                   }).then((data) => {
  //                     if (data.GetMessagesForConversationResult) {
  //                       data.orderedMessages.forEach(async (message) => {
  //                         if (
  //                           !message.IsDelivered &&
  //                           `${message.Sender.StaffGuid}` !== `${userGuid}`
  //                         ) {
  //                           await MarkMessageReceive({
  //                             messageGuid: message.Guid,
  //                           });
  //                         }
  //                       });
  //                     }
  //                   });
  //                 }
  //               }
  //             }
  //             if (data) {
  //               // sequential process
  //               GetConversationsArchive().then(async (data) => {
  //                 if (data.conversations && data.conversations.length > 0) {
  //                   let list = [];
  //                   for (let i = 0; i < data.conversations.length; i++) {
  //                     let Conversation = data.conversations[i];
  //                     Conversation.isGroup = !Conversation.Owner.FirstName;
  //                     list.push({
  //                       ...Conversation,
  //                       time: convertBackendDateLastMessage(
  //                         Conversation.LastMessage
  //                       ),
  //                     });
  //                   }
  //                   list.sort((a, b) => {
  //                     if (a.time > b.time) {
  //                       return -1;
  //                     }
  //                     if (a.time < b.time) {
  //                       return 1;
  //                     }
  //                     return 0;
  //                   });
  //                   if (list.length > 0)
  //                     setArchiveds(
  //                       list.filter((c) =>
  //                         c.Owner.Name.toUpperCase().includes(
  //                           filterName.toUpperCase()
  //                         )
  //                       )
  //                     );
  //                   for (let i = 0; i < list.length; i++) {
  //                     const Conversation = list[i];
  //                     if (Conversation.NumberOfUnreadMessages !== 0) {
  //                       GetMessagesForConversations({
  //                         Guid: Conversation.Guid,
  //                         maxNumberMessages: 40,
  //                       }).then((data) => {
  //                         if (data.GetMessagesForConversationResult) {
  //                           data.orderedMessages.forEach(async (message) => {
  //                             if (
  //                               !message.IsDelivered &&
  //                               `${message.Sender.StaffGuid}` !== `${userGuid}`
  //                             ) {
  //                               await MarkMessageReceive({
  //                                 messageGuid: message.Guid,
  //                               });
  //                             }
  //                           });
  //                         }
  //                       });
  //                     }
  //                   }
  //                 }
  //               });
  //             }
  //           });
  //           // GetConversationsArchive().then(async (data) => {
  //           //   if (data.conversations && data.conversations.length > 0) {
  //           //     let list = [];
  //           //     for (let i = 0; i < data.conversations.length; i++) {
  //           //       let Conversation = data.conversations[i];
  //           //       Conversation.isGroup = !Conversation.Owner.FirstName;
  //           //       list.push({
  //           //         ...Conversation,
  //           //         time: convertBackendDateLastMessage(
  //           //           Conversation.LastMessage
  //           //         ),
  //           //       });
  //           //     }
  //           //     list.sort((a, b) => {
  //           //       if (a.time > b.time) {
  //           //         return -1;
  //           //       }
  //           //       if (a.time < b.time) {
  //           //         return 1;
  //           //       }
  //           //       return 0;
  //           //     });
  //           //     if (list.length > 0)
  //           //       setArchiveds(
  //           //         list.filter((c) =>
  //           //           c.Owner.Name.toUpperCase().includes(
  //           //             filterName.toUpperCase()
  //           //           )
  //           //         )
  //           //       );
  //           //     for (let i = 0; i < list.length; i++) {
  //           //       const Conversation = list[i];
  //           //       if (Conversation.NumberOfUnreadMessages !== 0) {
  //           //         GetMessagesForConversations({
  //           //           Guid: Conversation.Guid,
  //           //           maxNumberMessages: 40,
  //           //         }).then((data) => {
  //           //           if (data.GetMessagesForConversationResult) {
  //           //             data.orderedMessages.forEach(async (message) => {
  //           //               if (
  //           //                 !message.IsDelivered &&
  //           //                 `${message.Sender.StaffGuid}` !== `${userGuid}`
  //           //               ) {
  //           //                 await MarkMessageReceive({
  //           //                   messageGuid: message.Guid,
  //           //                 });
  //           //               }
  //           //             });
  //           //           }
  //           //         });
  //           //       }
  //           //     }
  //           //   }
  //           // });
  //         } catch (error) {
  //           console.log(error);
  //         }
  //       }
  //     }
  //   }, REACT_APP_POOL_TIME_2);
  //   return () => clearInterval(interval);
  // }, [user, portal]);

  // useEffect(() => {
  //   const interval = setInterval(async () => {
  //     if (user && portal) {
  //       if (document.visibilityState === 'visible') {
  //         try {
  //           const GET_CALL_EVENT = getCallEvent();
  //           // promise fullfilled => call is in progress
  //           Promise.resolve(GET_CALL_EVENT).then((data) => {
  //             if (data?.GetCallEventResult === true) {
  //               // set call to 1 => call is in progress
  //               setCallEvents(data?.callEvent);
  //             } else {
  //               setCallEvents(null);
  //             }
  //           });
  //         } catch (error) {
  //           console.log(error);
  //         }
  //       }
  //     }
  //   }, 10000);
  //   return () => clearInterval(interval);
  // }, [user, portal]);
  // useEffect(() => {
  //   const maxTimeOut = setTimeout(async () => {
  //     if (user && portal) {
  //       if (document.visibilityState === 'visible') {
  //         try {
  //           const GET_CALL_EVENT = getCallEvent();
  //           // promise fullfilled => call is in progress
  //           Promise.resolve(GET_CALL_EVENT).then((data) => {
  //             if (data?.GetCallEventResult === true) {
  //               // set call to 1 => call is in progress
  //               setCallEvents(data?.callEvent);
  //             } else {
  //               setCallEvents(null);
  //             }
  //           });
  //         } catch (error) {
  //           console.log(error);
  //         }
  //       }
  //     }
  //   }, 10000);
  //   return () => clearTimeout(maxTimeOut);
  // }, [user, portal]);
  return (
    <ConversationContext.Provider
      value={{
        conversations,
        archiveds,
        reloadConversations,
        reloadArchivedConversations,
        openInfo,
        setOpenInfo,
        filterName,
        setFilterName,
        openChat,
        setOpenChat,
        coversationError,
        setConversationError,
        GetUserConversations,
        hasCall,
        setHasCall,
        isSSEConnectionActive,
        setSSEConnectionActive,
        conversationDecryptedKey,
        conversationDecryptedIV,
        // reloadGetCallEvent,
        EncryptMessageForConversation,
        getDecryptionParams,
      }}
    >
      {isDecrypting && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'white', // Completely invisible
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 99999,
          }}
        >
          <div style={{ position: 'relative', display: 'inline-flex' }}>
            {/* Static light-blue track background */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={60}
              style={{ color: '#e5f0fa' }}
            />
            {/* Moving dark-blue progress bar on top */}
            <CircularProgress
              variant="indeterminate"
              size={60}
              style={{
                color: colors.primary,
                position: 'absolute',
                left: 0,
                top: 0,
              }}
            />
          </div>
          <p
            style={{
              marginTop: 20,
              fontSize: 16,
              fontWeight: 'bold',
              color: '#333',
            }}
          >
            {intl.formatMessage({ id: 'SecuringConnectionAndLoadingChats' })}
          </p>
        </div>
      )}
      {alertOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(245, 247, 251, 0.85)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            zIndex: 1200, // Below Material-UI Dialog's z-index (1300) but covers page content
          }}
        />
      )}
      <AlertDialog
        open={alertOpen}
        type="error"
        title={alertTitle}
        message={alertMessage}
        handleCancel={() => {
          logout();
          setAlertOpen(false);
        }}
      />
      {children}
    </ConversationContext.Provider>
  );
};

function useConversation() {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error(
      'useConversation must be used within a ConversationProvider'
    );
  }
  return context;
}
export { ConversationProvider, ConversationContext, useConversation };
